// Security monitoring dashboard. Reads scan history written by
// /api/public/hooks/security-scan (daily pg_cron) and highlights new findings.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ADMIN_TOKEN_KEY = "security-monitor:admin-token:v1";

function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  let token = window.sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  if (!token) {
    token = window.prompt("Enter ADMIN_TOKEN to view security monitor") ?? "";
    if (token) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  }
  return token;
}

interface Finding {
  key: string;
  severity: "critical" | "high" | "medium" | "low";
  kind: string;
  object: string;
  detail: string;
}

interface Scan {
  id: string;
  scanned_at: string;
  db_findings: Finding[];
  dep_findings: Finding[];
  db_issue_count: number;
  dep_issue_count: number;
  critical_count: number;
  new_finding_keys: string[];
  notes: string | null;
}

export const Route = createFileRoute("/admin/security-monitor")({
  head: () => ({
    meta: [
      { title: "Security Monitor — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SecurityMonitorPage,
});

function severityVariant(sev: Finding["severity"]) {
  return sev === "critical" || sev === "high" ? "destructive" : "secondary";
}

function SecurityMonitorPage() {
  const [scans, setScans] = useState<Scan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  async function load() {
    setError(null);
    try {
      const res = await fetch("/api/admin/security-scans?limit=90", {
        headers: { "x-admin-token": getAdminToken() },
        cache: "no-store",
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      setScans(json.scans as Scan[]);
    } catch (err) {
      setError(String(err));
    }
  }

  async function scanNow() {
    setScanning(true);
    try {
      const res = await fetch("/api/public/hooks/security-scan", { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      await load();
    } catch (err) {
      setError(String(err));
    } finally {
      setScanning(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const latest = scans?.[0];
  const findings = latest ? [...latest.db_findings, ...latest.dep_findings] : [];
  const newKeys = new Set(latest?.new_finding_keys ?? []);
  const series = (scans ?? [])
    .slice()
    .reverse()
    .map((s) => ({
      date: new Date(s.scanned_at).toISOString().slice(5, 10),
      database: s.db_issue_count,
      dependencies: s.dep_issue_count,
      critical: s.critical_count,
    }));

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Security Monitor</h1>
          <p className="text-muted-foreground text-sm">
            Daily automated scan of database functions, RLS posture, and dependencies.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void load()}>
            Refresh
          </Button>
          <Button onClick={() => void scanNow()} disabled={scanning}>
            {scanning ? "Scanning…" : "Scan now"}
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-destructive mb-6 text-sm" role="alert">
          {error}
        </p>
      )}

      {latest && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Database findings</CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-semibold">{latest.db_issue_count}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Dependency findings</CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-semibold">{latest.dep_issue_count}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">High / critical</CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-semibold">{latest.critical_count}</CardContent>
          </Card>
        </div>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Findings over time</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="database" stroke="hsl(var(--primary))" />
              <Line type="monotone" dataKey="dependencies" stroke="hsl(var(--accent-foreground))" />
              <Line type="monotone" dataKey="critical" stroke="hsl(var(--destructive))" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Latest findings
            {latest && (
              <span className="text-muted-foreground ml-2 text-xs font-normal">
                {new Date(latest.scanned_at).toLocaleString()}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!scans && <p className="text-muted-foreground text-sm">Loading…</p>}
          {scans && findings.length === 0 && (
            <p className="text-sm">No open findings in the latest scan.</p>
          )}
          {findings.map((f) => (
            <div key={f.key} className="rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={severityVariant(f.severity)}>{f.severity}</Badge>
                <span className="font-medium">{f.object}</span>
                {newKeys.has(f.key) && <Badge variant="outline">new</Badge>}
              </div>
              <p className="text-muted-foreground mt-1 text-sm">{f.detail}</p>
              <p className="text-muted-foreground mt-1 text-xs">{f.kind}</p>
            </div>
          ))}
          {latest?.notes && (
            <p className="text-muted-foreground text-xs">Notes: {latest.notes}</p>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
